import Exception from "src/utils/exception";
import Constants from "src/utils/constants";
import DBOperationsComb from "src/utils/dbOperationsComb";
import { useFormPageStore } from "src/stores/formPageStore";

// Helper function to recursively find all page IDs
function _getAllPageIds(pages) {
  const pageIds = new Set();

  const findPageIds = (items) => {
    if (!Array.isArray(items)) return;

    for (const item of items) {
      if (item.page_id) {
        pageIds.add(item.page_id);
      }
      // Check nested structures
      if (item.list_items && Array.isArray(item.list_items)) {
        findPageIds(item.list_items);
      }
      if (item.pages && Array.isArray(item.pages)) {
        findPageIds(item.pages);
      }
    }
  };

  findPageIds(pages);
  return Array.from(pageIds);
}

async function getFormTemplatesList(config) {
  try {
    // Admin dashboard is online-only — skip RxDB and hit Supabase directly so
    // the list always reflects current server state (and so we don't wait on
    // an offline cache that this admin-only user-flow doesn't need).
    return await DBOperationsComb.fetchData(
      {
        tableName: Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_TABLE,
        ...config
      },
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return { error };
  }
}

async function getFormTemplateById(id, offlineFirst = true) {
  try {
    // Draft preview (?draft=1) passes offlineFirst=false so we bypass RxDB —
    // form_templates.draft_payload is intentionally NOT synced to the local
    // cache, so RxDB would return a stale or draft-less row.
    const _res = await DBOperationsComb.getData(
      Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_TABLE,
      id,
      offlineFirst
    );

    if (_res.error) throw _res.error;
    const formPageStore = useFormPageStore();
    for (const page of _res.data.pages) {
      if (!page.page_id) continue;
      // Processing single page
      const _pageRes = await formPageStore.getFormPageById(
        page.page_id,
        true,
        offlineFirst
      );
      if (_pageRes.error) throw _pageRes.error;
      page.pageObj = _pageRes.data;
    }
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return { error };
  }
}

/**
 * One form_templates row from the server, as stored — no pages loaded. For
 * the form builder, which loads pages itself and renders the draft envelope.
 *
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
async function getFormTemplateRow(id) {
  try {
    return await DBOperationsComb.getData(
      Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_TABLE,
      id,
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return { data: null, error: error.message };
  }
}

/**
 * The current live and draft version numbers of one template, straight from
 * the server — for the form builder's version-conflict recovery.
 *
 * @returns {Promise<{data: {id, version, draft_version}|null, error: string|null}>}
 *   `data` is null when no template has that id.
 */
async function getFormTemplateVersions(id) {
  try {
    const res = await DBOperationsComb.fetchData(
      {
        tableName: Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_TABLE,
        select: "id, version, draft_version",
        queryParams: [{ field: "id", value: id }],
        fetchDeleted: true,
        fetchAll: true
      },
      false
    );
    if (res?.error) return { data: null, error: res.error };
    return { data: res?.data?.[0] ?? null, error: null };
  } catch (error) {
    Exception.catchException(error);
    return { data: null, error: error.message };
  }
}

async function updateFormTemplate(data) {
  try {
    const _res = await DBOperationsComb.updateData(
      Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_TABLE,
      data
    );

    if (_res.error) throw _res.error;

    // If required_sync is being updated, update all child pages
    if ("required_sync" in data) {
      const formPageStore = useFormPageStore();
      const template = _res.data;
      if (template.pages && Array.isArray(template.pages)) {
        // Get all page IDs recursively
        const pageIds = _getAllPageIds(template.pages);
        // Update each child page
        for (const pageId of pageIds) {
          await formPageStore.updateFormPage({
            id: pageId,
            required_sync: data.required_sync
          });
        }
      }
    }

    return _res;
  } catch (error) {
    Exception.catchException(error);
    return { error };
  }
}

async function getDynamicReportForms() {
  try {
    return await DBOperationsComb.fetchData(
      {
        tableName: Constants.enums.TABLES_NAME_ENUM.FORM_TEMPLATES_TABLE,
        fetchDeleted: false,
        fetchAll: true,

        queryParams: [
          {
            field: "settings->>is_dynamic_report",
            value: true,
            opType: Constants.enums.COMPARISON_TYPE.EQ
          }
        ]
      },
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return { error };
  }
}

export default {
  getFormTemplatesList,
  getFormTemplateById,
  getFormTemplateRow,
  getFormTemplateVersions,
  updateFormTemplate,
  getDynamicReportForms
};
